import clsx from "clsx";
import { useRef } from "react";

const variants = {
  primary:   "bg-gradient-to-r from-violet-600 to-cyan-500 text-white shadow-lg shadow-violet-500/30 hover:shadow-violet-500/50 hover:from-violet-500 hover:to-cyan-400",
  secondary: "bg-nexus-s2 border border-nexus-border text-white hover:bg-nexus-s3 hover:border-violet-500/30",
  success:   "bg-gradient-to-r from-green-600 to-emerald-500 text-white shadow-lg shadow-green-500/25 hover:from-green-500 hover:to-emerald-400",
  danger:    "bg-red-500/12 border border-red-500/35 text-red-400 hover:bg-red-500/22 hover:border-red-500/55",
  ghost:     "bg-transparent text-gray-400 hover:text-white hover:bg-white/6",
  gold:      "bg-gradient-to-r from-yellow-500 to-amber-400 text-black font-extrabold shadow-lg shadow-yellow-500/30 hover:from-yellow-400 hover:to-amber-300",
  outline:   "border border-violet-500/45 text-violet-400 hover:bg-violet-500/12 hover:border-violet-400/70",
  fire:      "bg-gradient-to-r from-orange-600 to-red-500 text-white shadow-lg shadow-orange-500/25 hover:from-orange-500 hover:to-red-400",
};

const sizes = {
  xs:  "px-3 py-1.5 text-xs rounded-lg",
  sm:  "px-4 py-2 text-sm rounded-xl",
  md:  "px-5 py-2.5 text-sm rounded-xl",
  lg:  "px-6 py-3 text-base rounded-xl",
  xl:  "px-8 py-4 text-lg rounded-2xl",
};

export default function Button({
  children,
  onClick,
  variant = "primary",
  size = "md",
  full,
  disabled,
  loading,
  className = "",
  type = "button",
  icon,
}) {
  const btnRef = useRef(null);

  /* Ripple effect */
  const handleClick = (e) => {
    if (disabled || loading) return;

    const btn  = btnRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const x    = e.clientX - rect.left;
    const y    = e.clientY - rect.top;

    const ripple = document.createElement("span");
    const size   = Math.max(rect.width, rect.height) * 2;
    ripple.style.cssText = `
      position: absolute;
      width: ${size}px; height: ${size}px;
      left: ${x - size / 2}px; top: ${y - size / 2}px;
      background: radial-gradient(circle, rgba(255,255,255,0.22) 0%, transparent 70%);
      border-radius: 50%;
      transform: scale(0);
      animation: ripple-out 0.55s ease-out forwards;
      pointer-events: none;
    `;
    btn.style.position = "relative";
    btn.style.overflow = "hidden";
    btn.appendChild(ripple);
    setTimeout(() => ripple.remove(), 600);

    onClick?.(e);
  };

  return (
    <button
      ref={btnRef}
      type={type}
      onClick={handleClick}
      disabled={disabled || loading}
      className={clsx(
        "inline-flex items-center justify-center gap-2 font-bold transition-all duration-200",
        "active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed disabled:pointer-events-none",
        "hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-violet-500/40 focus:ring-offset-1 focus:ring-offset-nexus-bg",
        "overflow-hidden",
        variants[variant],
        sizes[size],
        full && "w-full",
        className
      )}
    >
      {loading ? (
        <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
      ) : icon}
      {children}
    </button>
  );
}

/* Inject ripple keyframe once */
if (typeof document !== "undefined" && !document.getElementById("ripple-style")) {
  const s = document.createElement("style");
  s.id = "ripple-style";
  s.textContent = `@keyframes ripple-out { to { transform: scale(1); opacity: 0; } }`;
  document.head.appendChild(s);
}
